/**
 * Web Push Popup Component - Emarsys Integration
 * 基于用户需求重新设计的Web Push弹窗组件
 */
class WebPushPopup {
  constructor() {
    console.log("[WebPushPopup] Constructor called");
    this.popup = document.getElementById("web-push-popup");
    if (!this.popup) {
      console.warn("[WebPushPopup] ❌ Web push popup element not found");
      return;
    }
    console.log("[WebPushPopup] ✅ Popup element found:", this.popup);

    this.config = this.getConfig();
    console.log("[WebPushPopup] Configuration loaded:", this.config);

    this.storageKeys = {
      sessionCount: "webpush_session_count",
      lastShown: "webpush_last_shown",
      userChoice: "webpush_user_choice",
    };

    // Emarsys相关变量
    this.customerId = this.popup?.dataset?.customerId || null;
    this.fieldId = parseInt(this.popup?.dataset?.emarsysFieldId) || 8113;
    this.isGeneralSubscribe = false;

    console.log("[WebPushPopup] Customer ID:", this.customerId);
    console.log("[WebPushPopup] Field ID:", this.fieldId);

    this.init();
  }

  /**
   * 获取配置信息
   */
  getConfig() {
    const popup = this.popup;
    return {
      desktopDelay: parseInt(popup.dataset.desktopDelay) * 1000 || 15000, // 15秒
      mobileDelay: parseInt(popup.dataset.mobileDelay) * 1000 || 15000, // 15秒
      showFrequency: parseInt(popup.dataset.showFrequency) || 2, // 2次
      hideDays: parseInt(popup.dataset.hideDays) || 7, // 7天
      deviceSetting: popup.dataset.deviceSetting || "all", // 所有设备
      includeUrls: popup.dataset.includeUrls
        ? popup.dataset.includeUrls.split("\n").filter((url) => url.trim())
        : [],
      excludeUrls: popup.dataset.excludeUrls
        ? popup.dataset.excludeUrls.split("\n").filter((url) => url.trim())
        : [],
    };
  }

  /**
   * 初始化
   */
  init() {
    console.log("[WebPushPopup] Initializing...");

    // 检查Emarsys SDK是否加载
    if (typeof WebEmarsysSdk === "undefined") {
      console.warn("[WebPushPopup] ❌ WebEmarsysSdk is not loaded, popup will not be shown");
      return;
    }
    console.log("[WebPushPopup] ✅ WebEmarsysSdk is loaded");

    // 检查是否应该显示弹窗
    console.log("[WebPushPopup] Checking if popup should be shown...");
    if (!this.shouldShowPopup()) {
      console.warn("[WebPushPopup] ❌ Popup should not be shown (failed conditions check)");
      return;
    }
    console.log("[WebPushPopup] ✅ All conditions passed, popup will be shown");

    // 绑定事件
    this.bindEvents();
    console.log("[WebPushPopup] ✅ Events bound");

    // 等待Emarsys SDK准备就绪
    this.waitForEmarsysReady(() => {
      // 设置触发延迟
      const delay = this.isMobile()
        ? this.config.mobileDelay
        : this.config.desktopDelay;

      console.log(`[WebPushPopup] Emarsys SDK ready. Will show popup in ${delay}ms (${this.isMobile() ? 'mobile' : 'desktop'} delay)`);

      setTimeout(() => {
        this.showPopup();
      }, delay);
    });
  }

  /**
   * 等待Emarsys SDK准备就绪
   */
  waitForEmarsysReady(callback) {
    if (typeof WebEmarsysSdk === "undefined") {
      console.error("WebEmarsysSdk is not available");
      return;
    }

    WebEmarsysSdk.push([
      "onReady",
      () => {
        callback();
      },
    ]);
  }

  /**
   * 检查是否应该显示弹窗
   */
  shouldShowPopup() {
    console.log("[WebPushPopup] --- Running shouldShowPopup checks ---");

    // 检查设备设置
    console.log("[WebPushPopup] Checking device compatibility...");
    if (!this.checkDeviceCompatibility()) {
      console.warn("[WebPushPopup] ❌ Device compatibility check failed");
      return false;
    }
    console.log("[WebPushPopup] ✅ Device compatibility check passed");

    // 检查URL设置
    console.log("[WebPushPopup] Checking URL targeting...");
    if (!this.checkUrlTargeting()) {
      console.warn("[WebPushPopup] ❌ URL targeting check failed");
      return false;
    }
    console.log("[WebPushPopup] ✅ URL targeting check passed");

    // 检查频率限制
    console.log("[WebPushPopup] Checking frequency limits...");
    if (!this.checkFrequencyLimits()) {
      console.warn("[WebPushPopup] ❌ Frequency limits check failed");
      return false;
    }
    console.log("[WebPushPopup] ✅ Frequency limits check passed");

    // 检查用户之前的选择
    console.log("[WebPushPopup] Checking user choice...");
    if (!this.checkUserChoice()) {
      console.warn("[WebPushPopup] ❌ User choice check failed");
      return false;
    }
    console.log("[WebPushPopup] ✅ User choice check passed");

    console.log("[WebPushPopup] --- All checks passed! ---");
    return true;
  }

  /**
   * 检查设备兼容性
   */
  checkDeviceCompatibility() {
    const isMobile = this.isMobile();
    console.log(`[WebPushPopup] Device check - isMobile: ${isMobile}, deviceSetting: ${this.config.deviceSetting}`);

    switch (this.config.deviceSetting) {
      case "desktop":
        const desktopResult = !isMobile;
        console.log(`[WebPushPopup] Desktop only mode, result: ${desktopResult}`);
        return desktopResult;
      case "mobile":
        const mobileResult = isMobile;
        console.log(`[WebPushPopup] Mobile only mode, result: ${mobileResult}`);
        return mobileResult;
      case "all":
      default:
        console.log(`[WebPushPopup] All devices mode, result: true`);
        return true;
    }
  }

  /**
   * 检查URL定向设置
   */
  checkUrlTargeting() {
    const currentPath = window.location.pathname;
    console.log(`[WebPushPopup] URL check - Current path: ${currentPath}`);
    console.log(`[WebPushPopup] Include URLs:`, this.config.includeUrls);
    console.log(`[WebPushPopup] Exclude URLs:`, this.config.excludeUrls);

    // 检查包含URL（如果设置了包含列表，则只在这些URL显示）
    if (this.config.includeUrls.length > 0) {
      let shouldInclude = false;
      for (const includeUrl of this.config.includeUrls) {
        if (currentPath.includes(includeUrl.trim())) {
          shouldInclude = true;
          console.log(`[WebPushPopup] ✅ Path matches include URL: ${includeUrl}`);
          break;
        }
      }
      if (!shouldInclude) {
        console.log(`[WebPushPopup] ❌ Path does not match any include URLs`);
        return false;
      }
    } else {
      console.log(`[WebPushPopup] No include URLs set, all paths allowed`);
    }

    // 检查排除URL
    if (this.config.excludeUrls.length > 0) {
      for (const excludeUrl of this.config.excludeUrls) {
        if (currentPath.includes(excludeUrl.trim())) {
          console.log(`[WebPushPopup] ❌ Path matches exclude URL: ${excludeUrl}`);
          return false;
        }
      }
      console.log(`[WebPushPopup] Path does not match any exclude URLs`);
    } else {
      console.log(`[WebPushPopup] No exclude URLs set`);
    }

    return true;
  }

  /**
   * 检查频率限制
   */
  checkFrequencyLimits() {
    // 调试模式：跳过频率检查
    if (window.webPushPopupDebugMode) {
      console.log(`[WebPushPopup] 🔧 DEBUG MODE: Skipping frequency limits check`);
      return true;
    }

    // 检查会话内显示次数
    const sessionCount =
      parseInt(sessionStorage.getItem(this.storageKeys.sessionCount)) || 0;
    console.log(`[WebPushPopup] Frequency check - Session count: ${sessionCount}, Max frequency: ${this.config.showFrequency}`);

    if (sessionCount >= this.config.showFrequency) {
      console.log(`[WebPushPopup] ❌ Session count limit reached (${sessionCount} >= ${this.config.showFrequency})`);
      console.log(`[WebPushPopup] 💡 TIP: Run "window.webPushPopup.reset()" to clear and try again`);
      return false;
    }

    // 检查隐藏天数
    const lastShown = localStorage.getItem(this.storageKeys.lastShown);
    if (lastShown) {
      const daysSinceLastShown =
        (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      console.log(`[WebPushPopup] Last shown: ${daysSinceLastShown.toFixed(2)} days ago, Hide days: ${this.config.hideDays}`);

      if (daysSinceLastShown < this.config.hideDays) {
        console.log(`[WebPushPopup] ❌ Hide days limit not reached (${daysSinceLastShown.toFixed(2)} < ${this.config.hideDays})`);
        console.log(`[WebPushPopup] 💡 TIP: Run "window.webPushPopup.reset()" and reload to clear this restriction`);
        return false;
      }
    } else {
      console.log(`[WebPushPopup] No last shown timestamp found`);
    }

    console.log(`[WebPushPopup] Frequency limits OK`);
    return true;
  }

  /**
   * 检查用户之前的选择
   */
  checkUserChoice() {
    // 调试模式：跳过用户选择检查
    if (window.webPushPopupDebugMode) {
      console.log(`[WebPushPopup] 🔧 DEBUG MODE: Skipping user choice check`);
      return true;
    }

    const userChoice = localStorage.getItem(this.storageKeys.userChoice);
    const lastShown = localStorage.getItem(this.storageKeys.lastShown);
    console.log(`[WebPushPopup] User choice check - Choice: ${userChoice || 'none'}`);

    if (userChoice === "later" && lastShown) {
      const daysSinceChoice =
        (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      console.log(`[WebPushPopup] User chose "later" ${daysSinceChoice.toFixed(2)} days ago`);

      if (daysSinceChoice < this.config.hideDays) {
        console.log(`[WebPushPopup] ❌ User chose "later" too recently (${daysSinceChoice.toFixed(2)} < ${this.config.hideDays} days)`);
        console.log(`[WebPushPopup] 💡 TIP: Run "window.webPushPopup.reset()" to clear this choice`);
        return false;
      }
    }

    if (userChoice === "allow" || userChoice === "denied") {
      console.log(`[WebPushPopup] ❌ User already made a permanent choice: ${userChoice}`);
      console.log(`[WebPushPopup] 💡 TIP: Run "window.webPushPopup.reset()" to clear this choice`);
      return false;
    }

    console.log(`[WebPushPopup] User choice OK - no blocking choice found`);
    return true;
  }

  /**
   * 检查是否为移动设备
   */
  isMobile() {
    return (
      window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    );
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    // 按钮事件
    const laterBtns = this.popup.querySelectorAll('[data-action="later"]');
    const allowBtn = this.popup.querySelector('[data-action="allow"]');

    laterBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleLater();
      });
    });

    if (allowBtn) {
      allowBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleAllow();
      });
    }

    // ESC键关闭
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.popup.classList.contains("show")) {
        this.handleLater();
      }
    });
  }

  /**
   * 显示弹窗
   */
  showPopup() {
    console.log("[WebPushPopup] 🚀 showPopup() called");

    if (!this.popup) {
      console.error("[WebPushPopup] ❌ Cannot show popup: popup element not found");
      return;
    }

    // 更新会话计数
    const sessionCount =
      parseInt(sessionStorage.getItem(this.storageKeys.sessionCount)) || 0;
    const newSessionCount = sessionCount + 1;
    sessionStorage.setItem(
      this.storageKeys.sessionCount,
      newSessionCount.toString()
    );
    console.log(`[WebPushPopup] Session count updated: ${sessionCount} → ${newSessionCount}`);

    // 记录显示时间
    const timestamp = Date.now().toString();
    localStorage.setItem(this.storageKeys.lastShown, timestamp);
    console.log(`[WebPushPopup] Last shown timestamp saved: ${timestamp}`);

    // 显示弹窗
    console.log(`[WebPushPopup] Adding 'show' class to popup...`);
    this.popup.classList.add("show");
    this.popup.setAttribute("aria-hidden", "false");
    console.log(`[WebPushPopup] ✅ Popup should now be visible!`);

    // 焦点管理
    const allowBtn = this.popup.querySelector('[data-action="allow"]');
    if (allowBtn) {
      allowBtn.focus();
      console.log(`[WebPushPopup] Focus set to Allow button`);
    }

    // 触发自定义事件
    this.dispatchEvent("webpush:popup:shown");
    console.log(`[WebPushPopup] Custom event 'webpush:popup:shown' dispatched`);
  }

  /**
   * 隐藏弹窗
   */
  hidePopup() {
    if (!this.popup) {
      console.error("Cannot hide popup: popup element not found");
      return;
    }

    this.popup.classList.remove("show");
    this.popup.setAttribute("aria-hidden", "true");

    // 触发自定义事件
    this.dispatchEvent("webpush:popup:hidden");
  }

  /**
   * 处理Later按钮点击
   */
  handleLater() {
    localStorage.setItem(this.storageKeys.userChoice, "later");
    localStorage.setItem(this.storageKeys.lastShown, Date.now().toString());

    this.hidePopup();

    // 触发自定义事件
    this.dispatchEvent("webpush:popup:later");
  }

  /**
   * 处理Allow按钮点击 - 使用Emarsys
   */
  handleAllow() {
    if (typeof WebEmarsysSdk === "undefined") {
      console.error("WebEmarsysSdk is not loaded");
      this.hidePopup();
      this.dispatchEvent("webpush:popup:error", { error: "SDK not loaded" });
      return;
    }

    // 检查浏览器是否支持通知
    if (!("Notification" in window)) {
      console.error("This browser does not support notifications");
      this.hidePopup();
      this.dispatchEvent("webpush:popup:error", {
        error: "Browser not supported",
      });
      return;
    }

    this.emarsysSubscribe();
  }

  /**
   * Emarsys订阅逻辑
   */
  emarsysSubscribe() {
    try {
      WebEmarsysSdk.push(() => {
        // 使用全局变量来跟踪订阅状态
        if (typeof window.emarsysGeneralSubscribeFlag === "undefined") {
          window.emarsysGeneralSubscribeFlag = false;
        }
        window.emarsysGeneralSubscribeFlag = true;
        this.isGeneralSubscribe = true;

        // 设置超时机制
        const subscribeTimeout = setTimeout(() => {
          console.error("Emarsys subscribe timeout");
          this.hidePopup();
          this.dispatchEvent("webpush:popup:error", {
            error: "Subscribe timeout",
          });
        }, 10000); // 10秒超时

        WebEmarsysSdk.subscribe();

        WebEmarsysSdk.push([
          "onSubscribe",
          (res) => {
            clearTimeout(subscribeTimeout);

            // 用户已订阅web push
            localStorage.setItem("emarsysSubscribe", "subscribe");
            localStorage.setItem(this.storageKeys.userChoice, "allow");

            if (this.customerId) {
              // 用户已登录状态，关联用户ID
              this.handleEmarsysLogin();
            }

            this.hidePopup();
            this.dispatchEvent("webpush:popup:subscribed", { success: true });
          },
        ]);

        WebEmarsysSdk.push([
          "onPermissionDenied",
          (res) => {
            clearTimeout(subscribeTimeout);

            // 用户拒绝了web push
            localStorage.setItem(this.storageKeys.userChoice, "denied");
            this.hidePopup();
            this.dispatchEvent("webpush:popup:denied", { success: false });
          },
        ]);
      });
    } catch (error) {
      console.error("Emarsys subscribe error:", error);
      this.hidePopup();
      this.dispatchEvent("webpush:popup:error", { error: error.message });
    }
  }

  /**
   * 处理Emarsys登录逻辑
   */
  handleEmarsysLogin() {
    WebEmarsysSdk.push(() => {
      WebEmarsysSdk.isSubscribed()
        .then((res) => {
          if (res) {
            const contactInfo = {
              fieldId: Number(this.fieldId),
              fieldValue: this.customerId,
            };
            WebEmarsysSdk.login(contactInfo)
              .then((res) => {
                console.log("Emarsys login result: " + res);
                if (res) {
                  localStorage.setItem("emarsysLogin", "login");
                }
              })
              .catch((err) => {
                console.error("Emarsys login error:", err);
              });
          }
        })
        .catch((err) => {
          console.error("Emarsys isSubscribed error:", err);
        });
    });
  }

  /**
   * 触发自定义事件
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * 公共API：手动显示弹窗
   */
  show() {
    if (this.shouldShowPopup()) {
      this.showPopup();
    }
  }

  /**
   * 公共API：手动隐藏弹窗
   */
  hide() {
    this.hidePopup();
  }

  /**
   * 公共API：重置状态
   */
  reset() {
    console.log("[WebPushPopup] 🔄 Resetting all storage...");
    localStorage.removeItem(this.storageKeys.userChoice);
    localStorage.removeItem(this.storageKeys.lastShown);
    localStorage.removeItem("emarsysSubscribe");
    localStorage.removeItem("emarsysLogin");
    sessionStorage.removeItem(this.storageKeys.sessionCount);
    console.log("[WebPushPopup] ✅ All storage cleared. Reload page to see popup again.");
  }

  /**
   * 公共API：调试信息
   */
  debug() {
    console.log("=== WebPushPopup Debug Info ===");
    console.log("Configuration:", this.config);
    console.log("Storage Keys:", this.storageKeys);

    const sessionCount = sessionStorage.getItem(this.storageKeys.sessionCount);
    const userChoice = localStorage.getItem(this.storageKeys.userChoice);
    const lastShown = localStorage.getItem(this.storageKeys.lastShown);

    console.log("Session Count:", sessionCount || "0");
    console.log("User Choice:", userChoice || "none");
    console.log("Last Shown:", lastShown ? new Date(parseInt(lastShown)).toLocaleString() : "never");

    if (lastShown) {
      const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      console.log(`Days since last shown: ${daysSince.toFixed(2)} (hide after ${this.config.hideDays} days)`);
    }

    console.log("Emarsys Subscribe:", localStorage.getItem("emarsysSubscribe") || "not set");
    console.log("Emarsys Login:", localStorage.getItem("emarsysLogin") || "not set");
    console.log("Customer ID:", this.customerId || "not logged in");
    console.log("Field ID:", this.fieldId);
    console.log("Is Mobile:", this.isMobile());
    console.log("WebEmarsysSdk loaded:", typeof WebEmarsysSdk !== "undefined");
    console.log("Debug Mode:", window.webPushPopupDebugMode || false);
    console.log("================================");
    console.log("💡 Useful commands:");
    console.log("  window.webPushPopup.reset() - Clear all storage");
    console.log("  window.webPushPopup.show() - Force show popup");
    console.log("  window.webPushPopupDebugMode = true - Enable debug mode (skip checks)");
    console.log("================================");
  }
}

// 初始化函数
function initWebPushPopup() {
  console.log("[WebPushPopup] initWebPushPopup() called, readyState:", document.readyState);

  // 确保DOM已加载且popup元素存在
  if (document.readyState === "loading") {
    console.log("[WebPushPopup] DOM still loading, waiting for DOMContentLoaded event...");
    document.addEventListener("DOMContentLoaded", initWebPushPopup);
    return;
  }

  console.log("[WebPushPopup] DOM is ready, looking for popup element...");
  const popup = document.getElementById("web-push-popup");
  if (!popup) {
    console.warn("[WebPushPopup] ❌ Web push popup element not found in DOM");
    return;
  }

  console.log("[WebPushPopup] ✅ Popup element found, creating WebPushPopup instance...");
  window.webPushPopup = new WebPushPopup();

  // 延迟初始化Emarsys相关逻辑，确保SDK完全加载
  setTimeout(() => {
    console.log("[WebPushPopup] Checking for WebEmarsysSdk for background logic...");
    if (typeof WebEmarsysSdk !== "undefined") {
      console.log("[WebPushPopup] ✅ WebEmarsysSdk available, initializing background logic...");
      initEmarsysLogic();
    } else {
      console.warn("[WebPushPopup] ❌ WebEmarsysSdk not available for background logic");
    }
  }, 1000);
}

console.log("[WebPushPopup] Script loaded, calling initWebPushPopup()...");
initWebPushPopup();

/**
 * 初始化Emarsys相关逻辑
 */
function initEmarsysLogic() {
  try {
    const popup = document.getElementById("web-push-popup");
    const customerId = popup ? popup.dataset.customerId || null : null;
    const fieldId = popup ? parseInt(popup.dataset.emarsysFieldId) || 8113 : 8113;

    if (typeof window.emarsysGeneralSubscribeFlag === "undefined") {
      window.emarsysGeneralSubscribeFlag = false;
    }

    // 检查设备兼容性
    function isIOS() {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    }

    function isStandaloneWebApp() {
      if (
        window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches
      ) {
        return true;
      }
      return false;
    }

    // 现有订阅者逻辑
    if (isIOS() && !isStandaloneWebApp()) {
      // iOS浏览器用户不自动订阅
    } else {
      WebEmarsysSdk.push([
        "onReady",
        function (res) {
          WebEmarsysSdk.push([
            "onPermissionGranted",
            function (res) {
              if (
                !localStorage.getItem("emarsysSubscribe") &&
                !window.emarsysGeneralSubscribeFlag
              ) {
                console.log("existing subscriber: web push migration");
                window.emarsysGeneralSubscribeFlag = true;
                WebEmarsysSdk.subscribe();
                localStorage.setItem("emarsysSubscribe", "subscribe");
              }
            },
          ]);
        },
      ]);
    }

    // 登录/登出逻辑
    if (customerId) {
      // 用户已登录状态
      if (!localStorage.getItem("emarsysLogin")) {
        WebEmarsysSdk.push(function (res) {
          WebEmarsysSdk.isSubscribed()
            .then(function (res) {
              const contactInfo = {
                fieldId: Number(fieldId),
                fieldValue: customerId,
              };
              WebEmarsysSdk.login(contactInfo).then(function (res) {
                console.log("Login Result: " + res);
                if (res) {
                  localStorage.setItem("emarsysLogin", "login");
                }
              });
            })
            .catch(function (err) {
              console.error("Is subscribed error", err);
            });
        });
      }
    } else {
      // 访客状态（匿名）
      if (localStorage.getItem("emarsysLogin")) {
        WebEmarsysSdk.push(function (res) {
          WebEmarsysSdk.isSubscribed()
            .then(function (res) {
              WebEmarsysSdk.logout()
                .then(function (res) {
                  console.log("Logout Result: " + res);
                  if (res) {
                    localStorage.removeItem("emarsysLogin");
                  }
                })
                .catch(function (err) {
                  console.error("Emarsys logout error:", err);
                });
            })
            .catch(function (err) {
              console.error("Is subscribed error", err);
            });
        });
      }
    }
  } catch (error) {
    console.error("Error in initEmarsysLogic:", error);
  }
}

// 导出类供外部使用
if (typeof module !== "undefined" && module.exports) {
  module.exports = WebPushPopup;
}
